// Custom metal nameplates, asset tags and data plates. Lead target (SERPs
// checked 2026-11-03): custom metal nameplates 320 (KD 0, $19.68); asset tag
// SERPs are online tag builders. Also: asset tags 2,900
// ($50.52 CPC), metal asset tags 1,900 ($27.84), metal nameplates 1,300,
// valve tags 720 ($33.57), data plates 720, stainless steel tags 390, custom
// asset tags 320 ($35.59), custom metal nameplates 320. Industrial label makers
// with online customizers rank; the pitch is short runs and NYC turnaround.
// No UL or MIL-spec claims.
const {
  engraving,
  quoted,
  setup,
  each,
  small,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'metalAssetTags',
  slug: 'metal-asset-tags',
  path: '/services/laser-engraving/metal-asset-tags/',
  noun: 'tag',
  crumb: 'Metal asset tags',
  title: 'Custom Metal Nameplates & Asset Tags | PrintAWorld',
  description:
    'Custom metal nameplates, asset tags, data plates and valve tags, laser marked in aluminum, stainless and brass with serials, barcodes and QR codes.',
  eyebrow: 'Metal tags & plates / laser marked in New York',
  heading:
    'Custom metal nameplates, asset tags and data plates, laser marked in NYC.',
  answer: `PrintAWorld laser-marks custom metal nameplates, asset tags, data plates and valve tags in New York, in anodized aluminum, stainless steel and brass. Serial numbers, barcodes and QR codes come from your spreadsheet, so every tag is different. Short runs and reorders are welcome. Engraving is a ${setup} setup plus ${each(small, 'tag')}; the blanks are ${quoted.toLowerCase()}.`,
  cta: 'Get a tag quote',
  heroImage: 'assetTags',
  ideaImages: ['dataPlate', 'valveTags', 'nameplate'],
  images: {
    assetTags: {
      file: 'asset-tags',
      alt: 'Black anodized aluminum asset tags with white laser-marked barcodes and serial numbers',
      caption: 'Asset tags with barcodes and serials',
    },
    dataPlate: {
      file: 'data-plate',
      alt: 'Brushed stainless data plate marked with model, serial and voltage, riveted to a machine panel',
      caption: 'A stainless data plate',
    },
    valveTags: {
      file: 'valve-tags',
      alt: 'Round brass and stainless valve tags engraved with numbers on a steel chain near pipes',
      caption: 'Numbered brass and steel valve tags',
    },
    nameplate: {
      file: 'nameplate',
      alt: 'Satin brass nameplate engraved Atlas & Co, Est. 1998 on a wood-paneled wall',
      caption: 'A brass nameplate for a wall',
    },
  },
  intro: {
    eyebrow: 'Short runs, fast',
    title: 'Custom metal nameplates and tags, every one numbered.',
    paragraphs: [
      'Label makers want thousands of tags and weeks of lead time. We laser-mark the run you need, from a few dozen asset tags to a new set of nameplates, and reorder the same design when you buy more equipment.',
      'Send the layout and the list of serials or codes. Every tag gets its own number, barcode or QR code from your file.',
    ],
  },
  ideas: {
    eyebrow: 'Tag and plate ideas',
    title: 'Data plates, valve tags and nameplates.',
  },
  uses: {
    eyebrow: 'Who orders them',
    title: 'Asset tags for IT, equipment and buildings.',
    items: [
      {
        label: 'IT and offices',
        title: 'Asset tags for laptops and gear',
        body: 'A barcode or QR code and a serial on every device, readable by your asset software.',
      },
      {
        label: 'Machine builders',
        title: 'Data plates and serial plates',
        body: 'Model, serial, ratings and your logo, marked in stainless for each machine you ship.',
      },
      {
        label: 'Plumbing and HVAC',
        title: 'Valve tags',
        body: 'Numbered brass or stainless tags for valves and pipe systems, with a hole for a chain.',
      },
      {
        label: 'Buildings',
        title: 'Nameplates and door plates',
        body: 'Office, suite and door plates in brass or brushed metal, matched across a floor.',
      },
      {
        label: 'Product makers',
        title: 'Maker’s plates',
        body: 'A small metal plate with your brand and number for furniture, instruments and products.',
      },
    ],
  },
  materials: {
    link: 'Aluminum, stainless or brass',
    eyebrow: 'Materials',
    title: 'Anodized aluminum, stainless steel and brass tags.',
    items: [
      {
        title: 'Anodized aluminum',
        body: 'Black or colored anodized aluminum marks bright white or silver: sharp barcodes and small text.',
      },
      {
        title: 'Stainless steel',
        body: 'A dark, permanent mark from our MOPA laser for data plates and tags that see weather and cleaning.',
      },
      {
        title: 'Brass',
        body: 'Engraved brass for valve tags and nameplates, with the classic look.',
      },
      {
        title: 'Holes and backing',
        body: 'Drilled or cut holes, rounded corners and adhesive backing are quoted with the blanks.',
      },
    ],
  },
  process: {
    title: 'From your layout to a box of numbered tags.',
    note: `Standard lead time is ${engraving.standardLeadTime}. The metal engraving area is up to ${engraving.workAreas.metal}.`,
    steps: [
      {
        title: 'Send the layout and list',
        body: 'Size, material, your logo, and a spreadsheet of serials, barcodes or QR codes.',
      },
      {
        title: 'Approve a proof',
        body: 'We send a proof of the first tags, then mark a sample and scan the codes.',
      },
      {
        title: 'We mark the run',
        body: 'Every tag is marked from your list, in order, and checked.',
      },
      {
        title: 'Packed in order',
        body: 'Tags are packed in sequence and shipped, or picked up in NYC.',
      },
    ],
  },
  pricing: {
    title: 'Metal tag pricing, per tag.',
    cta: 'Price my tags',
    rows: [
      { label: 'Marking, per tag', value: small },
      { label: 'Tag blanks: aluminum, stainless, brass', value: quoted },
      { label: 'Holes, corners, adhesive', value: quoted },
      { label: 'Reorders of the same design', value: quoted },
      { label: 'Rush', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Reorders',
    title: 'Keep the design on file, reorder the next numbers.',
    paragraphs: [
      'We keep your layout, so the next order picks up at the next serial number and looks the same as the last.',
      'Need tags on equipment that’s already installed? Send photos and sizes, and we plan the plates to fit.',
    ],
    cta: 'Plan a tag order',
  },
  faqTitle: 'Metal tag questions, answered.',
  faqs: [
    {
      question: 'How much do custom metal asset tags cost?',
      answer: costAnswer(
        small,
        `Tag blanks, holes, rounded corners and adhesive backing are ${quoted.toLowerCase()}. Larger runs cost less per tag.`
      ),
    },
    {
      question: 'Can every tag have its own serial number or barcode?',
      answer:
        'Yes. Send a spreadsheet of numbers or codes. We mark barcodes, QR codes, serials and text from the list, and scan a sample before the run.',
    },
    {
      question: 'Which metal is best for asset tags?',
      answer:
        'Anodized aluminum gives the sharpest, highest-contrast barcodes indoors. Stainless steel holds up better outdoors and to cleaning chemicals. Brass is mostly for valve tags and nameplates.',
    },
    {
      question: 'Are your tags rated to UL or military specs?',
      answer:
        'We don’t certify tags to UL, MIL or other standards. Tell us the environment, such as heat, solvents or outdoor use, and we recommend a metal and test it.',
    },
    {
      question: 'How large can a nameplate or data plate be?',
      answer: `The metal engraving area is up to ${engraving.workAreas.metal}. Larger signs can be made in wood or other materials; ask us.`,
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('tag'),
    },
    {
      question: 'How long does it take?',
      answer: timingAnswer(
        'Reorders of a design on file are usually the quickest.'
      ),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your tags.',
    body: 'Send the size, the metal, the layout and the list of numbers or codes. We’ll reply with a proof plan and a written quote.',
    checklist: [
      'Tag size and shape, with holes or adhesive',
      'Aluminum, stainless steel or brass',
      'Your logo and a list of serials or codes',
      'How many, and your in-hand date',
    ],
  },
  moreCards: ['metalBusinessCards', 'plaques', 'corporateGifts'],
  schema: {
    name: 'Metal Asset Tags and Nameplates',
    alternateName: [
      'Custom metal asset tags',
      'Metal nameplates',
      'Data plates',
      'Valve tags',
    ],
    serviceType: 'Laser-marked metal tags and plates',
  },
})
